import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, CheckCircle2, Circle, FileText, Sparkles, Plus,
  Trash2, Target, TrendingUp, ShieldCheck, GraduationCap, Info, Lock,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const PRIORITY = {
  urgent: { label: "Urgent", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  high: { label: "High Priority", cls: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30" },
  recommended: { label: "Recommended", cls: "bg-blue-500/15 text-blue-400 border-blue-500/25" },
  optional: { label: "Optional", cls: "bg-white/8 text-[#A1A1AA] border-white/15" },
  completed: { label: "Completed", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" },
};

function ScoreTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/8 bg-[#121214] p-3">
      <Icon className="w-4 h-4 text-[#D4AF37] mb-1.5" />
      <div className="font-heading text-lg text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#71717A]">{label}</div>
    </div>
  );
}

export default function AdvisorDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/advisor/dashboard").then(({ data }) => setD(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const runQuick = async (action, module) => {
    setBusy(true);
    try { const { data } = await api.post("/advisor/quick-action", { action, module }); setResult(data); }
    catch { toast.error("Could not run action"); } finally { setBusy(false); }
  };
  const addTask = async (rec) => {
    try {
      await api.post("/advisor/tasks", { title: rec.title, priority: rec.priority || "recommended", module: rec.module || "", path: rec.path || "" });
      toast.success("Added to your tasks & Progress Tracker"); load();
    } catch { toast.error("Could not add task"); }
  };
  const toggleTask = async (t) => {
    try { await api.patch(`/advisor/tasks/${t.task_id}`, { status: t.status === "done" ? "open" : "done" }); load(); }
    catch { toast.error("Could not update task"); }
  };
  const deleteTask = async (id) => { try { await api.delete(`/advisor/tasks/${id}`); load(); } catch { toast.error("Could not delete"); } };
  const genDoc = async (docType, allowed) => {
    if (!allowed) { toast.error("Upgrade your plan to generate this document"); return; }
    try {
      const { data } = await api.post("/advisor/generate-document", { doc_type: docType });
      if (data.needs_confirmation) {
        if (window.confirm(data.message)) { await api.post("/advisor/generate-document", { doc_type: docType, overwrite: true }); toast.success("Document replaced & saved to your Document Library"); }
        return;
      }
      toast.success(`${data.title} saved to your Document Library`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate document"); }
  };

  if (!d) return <p className="text-[#71717A] p-4">Loading your advisor…</p>;

  return (
    <div className="overflow-y-auto h-full pr-1" data-testid="advisor-dashboard">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[#A1A1AA]">Advising: <span className="text-white font-medium" data-testid="advising-business">{d.advising_business}</span>{d.entity && <span className="text-[#71717A]"> · {d.entity}</span>}</div>
        <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/25 rounded-full px-2 py-0.5">{d.tier} plan</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="advisor-scores">
        <ScoreTile icon={Target} label="Formation" value={`${d.scores.formation}%`} />
        <ScoreTile icon={TrendingUp} label="Credit" value={d.scores.credit} />
        <ScoreTile icon={ShieldCheck} label="Compliance" value={`${d.scores.compliance}%`} />
        <ScoreTile icon={GraduationCap} label="Funding ready" value={d.scores.funding_readiness != null ? `${d.scores.funding_readiness}%` : "—"} />
      </div>

      {d.top_action && (
        <button onClick={() => navigate(d.top_action.path)} data-testid="advisor-top-action"
          className="w-full text-left rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#151310] to-[#121214] p-5 mb-5 hover:border-[#D4AF37]/50 transition-colors">
          <div className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-[#D4AF37]" /><span className="text-[11px] uppercase tracking-wider text-[#D4AF37]">Most important next action</span></div>
          <div className="font-heading text-lg text-white">{d.top_action.title}</div>
          <div className="text-sm text-[#A1A1AA]">{d.top_action.why}</div>
        </button>
      )}

      <div className="mb-5">
        <h3 className="text-sm font-semibold text-white mb-2">Quick actions</h3>
        <div className="flex flex-wrap gap-2" data-testid="advisor-quick-actions">
          {d.quick_actions.map((qa) => (
            <button key={qa.id} onClick={() => runQuick(qa.id)} disabled={busy} data-testid={`quick-action-${qa.id}`}
              className="text-xs h-8 px-3 rounded-full bg-white/5 border border-white/15 text-[#D4D4D8] hover:border-[#D4AF37]/40 hover:text-white transition-colors disabled:opacity-50">{qa.label}</button>
          ))}
        </div>
      </div>

      {result && (
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#151310] p-5 mb-5" data-testid="quick-action-result">
          <div className="flex items-center justify-between mb-2"><h4 className="font-heading text-lg text-white">{result.title}</h4><button onClick={() => setResult(null)} className="text-xs text-[#71717A] hover:text-white">Dismiss</button></div>
          <div className="space-y-1 text-sm text-[#D4D4D8]">{result.body.map((line, i) => <div key={i}>{line}</div>)}</div>
          {result.cta && result.cta.path && (
            <button onClick={() => navigate(result.cta.path)} data-testid="quick-action-cta" className="mt-3 flex items-center gap-1.5 text-sm fv-gold-gradient text-[#0A0A0B] rounded-full px-4 py-2 font-semibold">{result.cta.action_label || "Open"} {result.cta.title} <ArrowRight className="w-4 h-4" /></button>
          )}
        </div>
      )}

      <h3 className="text-sm font-semibold text-white mb-2">Recommendations</h3>
      <div className="space-y-2 mb-5" data-testid="advisor-recommendations">
        {d.recommendations.length === 0 && <p className="text-sm text-emerald-400">You're all caught up — great work!</p>}
        {d.recommendations.map((r) => {
          const p = PRIORITY[r.priority] || PRIORITY.optional;
          return (
            <div key={r.id} className="rounded-xl border border-white/8 bg-[#121214] p-4" data-testid={`rec-${r.id}`}>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${p.cls}`}>{p.label}</span>
                <span className="text-white font-medium text-sm">{r.title}</span>
              </div>
              <p className="text-sm text-[#A1A1AA] mb-1">{r.why}</p>
              <p className="text-[11px] text-[#52525B] flex items-center gap-1 mb-3"><Info className="w-3 h-3" /> Based on: {r.data_source}</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => navigate(r.path)} data-testid={`rec-open-${r.id}`} className="text-xs h-8 px-3 rounded-full fv-gold-gradient text-[#0A0A0B] font-semibold">{r.action_label} {r.module}</button>
                <button onClick={() => addTask(r)} data-testid={`rec-addtask-${r.id}`} className="flex items-center gap-1 text-xs h-8 px-3 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors"><Plus className="w-3 h-3" /> Add task</button>
              </div>
            </div>
          );
        })}
      </div>

      <h3 className="text-sm font-semibold text-white mb-2">My tasks {d.open_tasks.length > 0 && <span className="text-[#71717A] font-normal">({d.open_tasks.length} open)</span>}</h3>
      <div className="space-y-2 mb-5" data-testid="advisor-tasks">
        {d.open_tasks.length === 0 && d.recent_tasks.length === 0 && <p className="text-sm text-[#52525B]">No tasks yet — add one from a recommendation.</p>}
        {[...d.open_tasks, ...d.recent_tasks].map((t) => (
          <div key={t.task_id} className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#121214] p-3" data-testid={`task-${t.task_id}`}>
            <button onClick={() => toggleTask(t)} data-testid={`task-toggle-${t.task_id}`}>{t.status === "done" ? <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> : <Circle className="w-4 h-4 text-[#52525B]" />}</button>
            <span className={`text-sm flex-1 ${t.status === "done" ? "text-[#71717A] line-through" : "text-white"}`}>{t.title}</span>
            {t.due_date && <span className="text-[11px] text-[#71717A]">{t.due_date}</span>}
            <button onClick={() => deleteTask(t.task_id)} data-testid={`task-delete-${t.task_id}`} className="text-[#71717A] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      <h3 className="text-sm font-semibold text-white mb-2">Generate a document</h3>
      <div className="grid sm:grid-cols-2 gap-2 mb-4" data-testid="advisor-docs">
        {d.doc_types.map((doc) => (
          <button key={doc.id} onClick={() => genDoc(doc.id, doc.allowed)} data-testid={`gendoc-${doc.id}`}
            className={`flex items-center gap-2 rounded-xl border p-3 text-sm transition-colors ${doc.allowed ? "border-white/8 bg-[#121214] text-[#D4D4D8] hover:border-[#D4AF37]/40" : "border-white/5 bg-[#0d0d0f] text-[#52525B]"}`}>
            {doc.allowed ? <FileText className="w-4 h-4 text-[#D4AF37] shrink-0" /> : <Lock className="w-4 h-4 shrink-0" />}
            <span className="flex-1 text-left">{doc.label}</span>
            {!doc.allowed && <span className="text-[9px] uppercase text-[#71717A]">{doc.tier}</span>}
          </button>
        ))}
      </div>

      <p className="text-[11px] text-[#52525B] flex items-start gap-1.5 border-t border-white/8 pt-3" data-testid="advisor-disclaimer"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{d.disclaimer}</p>
    </div>
  );
}
